import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { CheckoutProduct } from "tm-shared/types";

import { IgnoreRemainingConfirm } from "@/domains/product";
import { useMultiTap } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { ProductCardWithCounterUI } from "@/pages/WorkPlan/Tasks/components/ProductCardWithCounterUI";
import { addIgnoreRemainingOrder } from "@/store/slices/offline/offline.slice";

import {
    StyledIgnoreStockButton,
    StyledIgnoreStockWrapper,
} from "./CheckoutProductCard.styled";

interface CheckoutProductCardProps {
    barcode: string;
    name: string;
    itemsLeft?: number;
    checkout?: CheckoutProduct;
    price: number;
    orderId: string;
    ignoreRemainingsOrders: Record<string, string[]>;
    isLast: boolean;
    onView: () => void;

    onAddProductLocal?: (barcode: string, name: string, price: number) => void;
    onProductCountChangeLocal?: (barcode: string, newCount: number) => void;
}

export const CheckoutProductCard: FC<CheckoutProductCardProps> = memo(
    ({
        barcode,
        name,
        itemsLeft,
        checkout,
        price,
        orderId,
        ignoreRemainingsOrders,
        isLast,
        onView,
        onAddProductLocal,
        onProductCountChangeLocal,
    }) => {
        const dispatch = useAppDispatch();

        const [isModalOpened, setIsModalOpened] = useState(false);
        const [showIgnoreWrapper, setShowIgnoreWrapper] = useState(false);
        const [isCountDisabled, setIsCountDisabled] = useState(false);
        const [ignoreRemainings, setIgnoreRemaining] = useState(false);

        const confirmIgnoreRemaining = useCallback(() => {
            dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
            setIsCountDisabled(false);
            setIgnoreRemaining(true);
            setShowIgnoreWrapper(false);
            setIsModalOpened(false);
        }, [dispatch, orderId, barcode]);

        const cancelIgnoreRemaining = useCallback(() => {
            setIsCountDisabled(true);
            setShowIgnoreWrapper(false);
            setIsModalOpened(false);
        }, []);

        const handleAddToCheckout = useCallback(() => {
            onAddProductLocal?.(barcode, name, price);
        }, [barcode, onAddProductLocal, name, price]);

        const handleDoubleClick = useCallback(() => {
            setShowIgnoreWrapper(true);
        }, []);

        const handleTap = useMultiTap(handleDoubleClick);

        const handleIgnoreRemaining = useCallback(() => {
            if (
                Object.prototype.hasOwnProperty.call(
                    ignoreRemainingsOrders,
                    orderId,
                )
            ) {
                dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
                setIgnoreRemaining(true);
                setIsCountDisabled(false);
                setShowIgnoreWrapper(false);
            } else {
                setIsModalOpened(true);
            }
        }, [ignoreRemainingsOrders, orderId, barcode, dispatch]);

        const handleCheckoutCountChange = useCallback(
            (newCount: number) => {
                if (onProductCountChangeLocal) {
                    onProductCountChangeLocal(barcode, newCount);
                }
            },
            [onProductCountChangeLocal, barcode],
        );

        const counterMax = useMemo(
            () =>
                ignoreRemainings
                    ? 99999
                    : Math.max(0, (itemsLeft ?? 0) + (checkout?.apiCount ?? 0)),
            [ignoreRemainings, itemsLeft, checkout?.apiCount],
        );

        useEffect(() => {
            const isEmptyStockOrPrice = !itemsLeft || !price;
            const isNotInCheckout = !checkout?.count;
            setIsCountDisabled(isEmptyStockOrPrice && isNotInCheckout);
        }, [itemsLeft, price, checkout]);

        useEffect(() => {
            if (ignoreRemainingsOrders[orderId]?.includes(barcode)) {
                setIgnoreRemaining(true);
            }
        }, [ignoreRemainingsOrders, orderId, barcode]);

        const remaining =
            (itemsLeft ?? 0) +
            (checkout?.apiCount ?? 0) -
            (checkout?.count ?? 0);

        const additionalInfo = (
            <>
                <IgnoreRemainingConfirm
                    open={isModalOpened}
                    onConfirm={confirmIgnoreRemaining}
                    onClose={cancelIgnoreRemaining}
                />

                {showIgnoreWrapper && (
                    <StyledIgnoreStockWrapper>
                        <StyledIgnoreStockButton
                            type="primary"
                            onClick={handleIgnoreRemaining}
                        >
                            Ігнорувати залишки
                        </StyledIgnoreStockButton>
                    </StyledIgnoreStockWrapper>
                )}
            </>
        );

        const currentRemaining = ignoreRemainings ? "\u221E" : remaining;

        const isAddMode = !checkout;

        return (
            <ProductCardWithCounterUI
                name={name}
                barcode={barcode}
                additionalInfo={additionalInfo}
                remaining={currentRemaining}
                onRemainingHandleTap={handleTap}
                isLast={isLast}
                onView={onView}
                {...(isAddMode
                    ? {
                          counterType: "add" as const,
                          isAddDisabled: isCountDisabled,
                          onAddTouchStart: isCountDisabled
                              ? handleTap
                              : handleAddToCheckout,
                      }
                    : {
                          counterType: "counter" as const,
                          counterValue: checkout.count,
                          onCounterChange: handleCheckoutCountChange,
                          counterMin: 0,
                          counterMax,
                      })}
            />
        );
    },
);

CheckoutProductCard.displayName = "CheckoutProductCard";
